import { doc, getDoc, collection, getDocs, writeBatch } from 'firebase/firestore';
import { db } from '../../firebase/firebase';
import { MemberCode } from '../../types/organisation';
import { isDemoMode } from '../../utilities/demoMode';
import { DemoDataManager } from '../../services/demoDataManager';
import userStore from '../../stores/UserStore';
import { BatchManager } from '../base/batchUtils';

/**
 * Save generated codes for an organisation
 */
export async function saveGeneratedCodes(
  orgId: string,
  codes: MemberCode[]
): Promise<void> {
  try {
    const codesCollection = collection(db, 'Organisations', orgId, 'generatedCodes');
    const batchManager = new BatchManager();

    // Delete existing codes
    const existingCodes = await getDocs(codesCollection);
    for (const docSnapshot of existingCodes.docs) {
      await batchManager.addOperation(batch => {
        batch.delete(docSnapshot.ref);
      });
    }

    // Add new codes
    for (const code of codes) {
      await batchManager.addOperation(batch => {
        const docRef = doc(codesCollection);
        const { qrCode, ...codeData } = code; // Remove qrCode as it's regenerated on load
        batch.set(docRef, codeData);
      });
    }

    await batchManager.commit();
  } catch (error: any) {
    console.error('Error saving generated codes:', error);
    console.error('Error details:', {
      orgId,
      errorCode: error.code,
      errorMessage: error.message
    });
    throw error;
  }
}

/**
 * Get generated codes for an organisation
 */
export async function getGeneratedCodes(orgId: string) {
  try {
    // Check if in demo mode and organisational tutorial is active
    if (isDemoMode()) {
      const tutorialFlow = localStorage.getItem('demo_tutorial_flow');
      if (tutorialFlow === 'organisational-projects-tutorial') {
        const demoOrg = DemoDataManager.getDemoOrganisation();
        if (demoOrg && demoOrg.id === orgId) {
          // Return empty array for demo - no generated codes needed for tutorial
          return [];
        }
      }
    }

    const codesCollection = collection(db, 'Organisations', orgId, 'generatedCodes');
    
    // Get the user's role in the organisation
    const userDoc = await getDoc(doc(db, 'Users', userStore.currentUser?.id || ''));
    const userData = userDoc.data();
    
    const snapshot = await getDocs(codesCollection);
    
    return snapshot.docs.map(doc => doc.data());
  } catch (error: any) {
    console.error('Error getting generated codes:', error);
    console.error('Error details:', {
      orgId,
      errorCode: error.code,
      errorMessage: error.message,
      userId: userStore.currentUser?.id
    });
    throw error;
  }
}

/**
 * Delete all generated codes for an organisation
 */
export async function deleteAllGeneratedCodes(organisationId: string): Promise<void> {
  const codesRef = collection(db, 'Organisations', organisationId, 'generatedCodes');
  const codesSnapshot = await getDocs(codesRef);
  
  // Use BatchManager for automatic batch handling
  const batchManager = new BatchManager();
  
  for (const docSnapshot of codesSnapshot.docs) {
    await batchManager.addOperation(batch => {
      batch.delete(docSnapshot.ref);
    });
  }
  
  await batchManager.commit();
}
